'use client';

import { useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { traceAction } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';
import {
  asPublicDocId,
  createNewDoc,
  type DocVisibility,
  type FeedItemSuggestedQuestion,
} from '@/lib/api';

/** Matches the original suggested-question chip height on document threads. */
const SUGGESTED_QUESTION_HEIGHT = 32;

/** Gives the edit action a wide target without increasing the shared pill height. */
const EDIT_ACTION_WIDTH = SUGGESTED_QUESTION_HEIGHT * 2;

interface SuggestedQuestionChipsProps {
  questions: readonly FeedItemSuggestedQuestion[];
  /** Moves a question into the adjacent composer without posting it. */
  onEdit: (question: FeedItemSuggestedQuestion) => void;
  /** Applies the visibility inherited by the document-thread composer. */
  visibility: DocVisibility;
}

/** Renders feed questions with distinct immediate-post and edit-before-post actions. */
export function SuggestedQuestionChips({
  questions,
  onEdit,
  visibility,
}: SuggestedQuestionChipsProps) {
  const router = useRouter();
  const requestInFlightRef = useRef(false);
  const [postingQuestionKey, setPostingQuestionKey] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  const cornerRadius = SUGGESTED_QUESTION_HEIGHT / 2;

  /** Identifies a question consistently across its paired controls and pending state. */
  const questionKey = (question: FeedItemSuggestedQuestion): string =>
    `${question.source_doc_id}:${question.text}`;

  const postQuestion = async (question: FeedItemSuggestedQuestion): Promise<void> => {
    if (requestInFlightRef.current) {
      return;
    }

    requestInFlightRef.current = true;
    setPostingQuestionKey(questionKey(question));
    setPostError(null);
    try {
      const responseData = await traceAction(FEEDBACK_ACTION.POST_SUGGESTED_QUESTION, async () => {
        return createNewDoc({
          content: question.text,
          citation: question.source_doc_id,
          ...(question.topic_cref ? { topic: question.topic_cref } : {}),
          visibility,
        });
      });
      const docId = asPublicDocId(responseData.doc_id);
      if (!docId) {
        throw new Error('Invalid new-document response');
      }
      router.push(`/doc/${docId}?processing_started=1`);
    } catch (error) {
      console.error('Error posting suggested question:', error);
      setPostError("We couldn't post that question. Please try again.");
      requestInFlightRef.current = false;
      setPostingQuestionKey(null);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        gap: 1,
        mt: 'auto',
        pt: 1,
      }}
    >
      {postError && (
        <Alert severity="error" sx={{ flexBasis: '100%' }}>
          <Typography variant="body2">{postError}</Typography>
        </Alert>
      )}
      {questions.map((question) => {
        const key = questionKey(question);
        const isPostingQuestion = postingQuestionKey === key;

        return (
          <ButtonGroup
            key={key}
            size="small"
            variant="outlined"
            color="inherit"
            disabled={postingQuestionKey !== null}
            aria-label={`Suggested question: ${question.text}`}
            sx={{
              height: SUGGESTED_QUESTION_HEIGHT,
              maxWidth: '100%',
              borderRadius: cornerRadius,
              color: 'var(--mui-palette-text-primary)',
              '& .MuiButton-root': {
                borderColor: 'var(--mui-palette-divider)',
                '&:hover': {
                  borderColor: 'var(--mui-palette-divider)',
                  backgroundColor: 'var(--mui-palette-action-hover)',
                },
              },
            }}
          >
            <Button
              type="button"
              onClick={() => void postQuestion(question)}
              startIcon={isPostingQuestion ? <CircularProgress size={14} aria-hidden /> : undefined}
              sx={{
                height: SUGGESTED_QUESTION_HEIGHT,
                minHeight: SUGGESTED_QUESTION_HEIGHT,
                minWidth: 0,
                justifyContent: 'flex-start',
                overflow: 'hidden',
                borderTopLeftRadius: cornerRadius,
                borderBottomLeftRadius: cornerRadius,
                px: 1,
                py: 0,
                textAlign: 'left',
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography
                variant="caption"
                component="span"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {question.text}
              </Typography>
            </Button>
            <Tooltip title={postingQuestionKey === null ? 'Edit before asking' : ''}>
              <Button
                type="button"
                aria-label={`Edit “${question.text}” before asking`}
                onClick={() => onEdit(question)}
                sx={{
                  height: SUGGESTED_QUESTION_HEIGHT,
                  minHeight: SUGGESTED_QUESTION_HEIGHT,
                  borderTopRightRadius: cornerRadius,
                  borderBottomRightRadius: cornerRadius,
                  px: 0,
                  '&&': {
                    width: EDIT_ACTION_WIDTH,
                    minWidth: EDIT_ACTION_WIDTH,
                  },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 'inherit' }} />
              </Button>
            </Tooltip>
          </ButtonGroup>
        );
      })}
    </Box>
  );
}
